import React, { useState } from 'react'
import Badge from 'core/elements/badge'
import { StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import { GridCellProps } from 'core/elements/grid/hooks/useGridRows'
import { getMachineStatus } from 'k8s/components/capacity-and-health/ListWorkerNodes'
import MachineErrorModal from './MachineErrorModal'
import { MachineHealth, MachineHealthState } from './machineHealth'

export function getMachineStatusCellModel(
  phase: string,
  health: MachineHealth,
): StatusCellModel & { clickable: boolean } {
  switch (health?.state) {
    case MachineHealthState.Failed:
      return { variant: 'error', label: 'Failed', clickable: !!health.message }
    case MachineHealthState.Degraded:
      return {
        variant: 'error',
        label:
          health?.reason === 'ControlPlaneUnreachable'
            ? 'Connection Lost'
            : health?.reason === 'BYOHostsUnavailable'
              ? 'No Host Available'
              : `${phase || 'Provisioning'} - Error`,
        clickable: !!health.message,
      }
    case MachineHealthState.Slow:
      return { variant: 'default', label: 'Taking Longer', clickable: !!health.message }
    case MachineHealthState.Stuck:
      return {
        variant: 'warning',
        label:
          health?.reason === 'InfrastructureProvisionTimeout'
            ? 'Provisioning Stuck'
            : 'Not Joining',
        clickable: !!health.message,
      }
    default:
      return { ...getMachineStatus(phase), clickable: false }
  }
}

export default function MachineStatusCell({ item }: GridCellProps<any>) {
  const [showModal, setShowModal] = useState(false)
  const health: MachineHealth = item?.machineHealth
  const { variant, label, clickable } = getMachineStatusCellModel(item?.phase, health)

  return (
    <>
      <Badge
        variant={variant}
        text={label}
        // Status labels are short and bounded ("Provisioning - Error" is the
        // longest at 20). The Badge default ellipsisAt=15 clipped it to
        // "Provisioning - ..." and hid the "Error", so widen it to fit.
        ellipsisAt={30}
        tooltipBody={clickable ? 'Click to view error details' : undefined}
        onClick={
          clickable
            ? (e) => {
                e.stopPropagation()
                setShowModal(true)
              }
            : undefined
        }
      />
      {showModal && (
        <MachineErrorModal
          machineName={item?.name || item?.metadata?.name}
          reason={health?.reason}
          message={health?.message}
          instanceId={
            item?.instanceId ||
            item?.openStackServer?.status?.instanceID ||
            (item?.spec?.providerID?.startsWith('openstack:///')
              ? item?.spec?.providerID?.replace('openstack:///', '')
              : undefined)
          }
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  )
}
